import React, { useContext } from "react";
import { LineupContext } from "../context/LineupContext";
import { DEF_SLOT_KEYS, FOR_SLOT_KEYS, GOAL_SLOT_KEYS } from "../types";
import { useTranslation } from "@root/misc/useTranslation";
import _ from "lodash";

export interface AmountOf {
  amount: number;
  of: number;
}

const STAT_TYPES = ["goal", "defense", "forward"] as const;

interface Props {
  stats: ({ type: (typeof STAT_TYPES)[number] } & AmountOf)[];
  budget: AmountOf;
}

export const LineupStatsInner: React.FC<Props> = ({ stats, budget }) => {
  const { t } = useTranslation();

  return (
    <div className="neo__lineupStats">
      <div className="neo__lineupStats__container">
        {_.map(stats, (st) => {
          const className = [
            "neo__lineupStats__stat",
            st.amount === st.of
              ? "neo__lineupStats__stat--valid"
              : "neo__lineupStats__stat--error",
          ].join(" ");
          const labelClassName = [
            "neo__lineupStats__amountOf__label",
            st.amount !== st.of
              ? "neo__lineupStats__amountOf__label--error"
              : "",
          ].join(" ");

          return (
            <div key={st.type} className={className}>
              <span className="neo__lineupStats__amountOf">
                <strong>{st.amount}</strong>
                <span>{st.of}</span>
              </span>
              <span className={labelClassName}>
                {t(`lineup_stats.${st.type}`)}
              </span>
            </div>
          );
        })}
        <div className="neo__lineupStats__spring" />
        <div key={"budget"} className={"neo__lineupStats__budget"}>
          <span
            className={`neo__lineupStats__amountOf ${
              budget.amount < 0 ? "neo__lineupStats__amountOf--invalid" : ""
            }`}
          >
            <strong>{budget.amount}</strong>
            <span>{budget.of}</span>
          </span>
          <span className="neo__lineupStats__amountOf__label">
            {t(`lineup_stats.budget`)}
          </span>
        </div>
      </div>
    </div>
  );
};

export const LineupStats: React.FC = () => {
  const { lineup } = useContext(LineupContext);
  const props: Props = {
    stats: [
      {
        type: "goal",
        amount: GOAL_SLOT_KEYS.map((k) => !!lineup?.slots[k]?.pick).filter(
          (val) => val
        ).length,
        of: GOAL_SLOT_KEYS.length,
      },
      {
        type: "defense",
        amount: DEF_SLOT_KEYS.map((k) => !!lineup?.slots[k]?.pick).filter(
          (val) => val
        ).length,
        of: DEF_SLOT_KEYS.length,
      },
      {
        type: "forward",
        amount: FOR_SLOT_KEYS.map((k) => !!lineup?.slots[k]?.pick).filter(
          (val) => val
        ).length,
        of: FOR_SLOT_KEYS.length,
      },
    ],
    budget: {
      amount: lineup?.budget_left ?? 0,
      of: lineup?.total_budget ?? 0,
    },
  };
  return <LineupStatsInner {...props} />;
};

export const LineupStatsSandbox: React.FC = () => {
  return (
    <div>
      <h3>All complete</h3>
      <LineupStatsInner
        budget={{ amount: 2, of: 80 }}
        stats={[
          {
            type: "goal",
            amount: GOAL_SLOT_KEYS.length,
            of: GOAL_SLOT_KEYS.length,
          },
          {
            type: "defense",
            amount: DEF_SLOT_KEYS.length,
            of: DEF_SLOT_KEYS.length,
          },
          {
            type: "forward",
            amount: FOR_SLOT_KEYS.length,
            of: FOR_SLOT_KEYS.length,
          },
        ]}
      />
      <h3>All incomplete</h3>
      <LineupStatsInner
        budget={{ amount: 63, of: 80 }}
        stats={[
          { type: "goal", amount: 1, of: GOAL_SLOT_KEYS.length },
          { type: "defense", amount: 0, of: DEF_SLOT_KEYS.length },
          { type: "forward", amount: 3, of: FOR_SLOT_KEYS.length },
        ]}
      />
    </div>
  );
};
